import React from 'react'
import type { KambiHistoryCoupon } from '@mobi/api-types/kambi'
import { KambiRewardType } from '@mobi/api-types/kambi/player/reward'
import { PromoBanner } from '@mobi/betslip/Components/Common/PromoBanner'
import { SummaryLine } from '@mobi/betslip/Components/Common/SummaryLine'
import { spacing } from '@mobi/component-library/Foundations'
import { Flex } from '@mobi/component-library/Misc/Flex'
import { formatKambiCurrencyToDollarDecimal } from '@mobi/sports-offering/helpers/currency/currencyFormat'
import { toMoney } from '@mobi/utils/money'
import { Single } from '../BetInfo/Components/Single'
import { LegsStatusLine, Multi, SameGameMulti } from './Components'

/** Used Primarily for Bet History Coupon (top level summary, no detail) */
export const BetSummary: React.FC<{
  historyCoupon: KambiHistoryCoupon
  betType: string
}> = ({ historyCoupon, betType }) => {
  const { bets } = historyCoupon
  const bet = bets[0]
  const payout = toMoney(
    formatKambiCurrencyToDollarDecimal(bet.potentialPayoutBoosted ?? bet.potentialPayout)
  )
  const stake = toMoney(formatKambiCurrencyToDollarDecimal(bet.stake))

  const isMulti = betType === 'multi'
  const isSGM = betType === 'same-game-multi'

  const shouldShowLegStatuses = isMulti || isSGM
  const rewardText = getRewardText(historyCoupon.rewardType)

  return (
    <>
      {betType === 'single' && (
        <Single
          betRef={bet.betRef}
          betStatus={bet.betStatus}
          event={historyCoupon.events[0]}
          outcome={historyCoupon.outcomes[0]}
          betOffers={historyCoupon.betOffers}
          playedOdds={bet.playedOdds}
          betOddsBoosted={bet.betOddsBoosted}
        />
      )}

      {isMulti && <Multi historyCoupon={historyCoupon} />}

      {isSGM && <SameGameMulti historyCoupon={historyCoupon} />}

      <Flex flexDirection='column' gap={spacing.smx2}>
        {shouldShowLegStatuses && (
          <LegsStatusLine historyCoupon={historyCoupon} betType={betType} />
        )}

        <SummaryLine left='Stake' right={stake} />
        <SummaryLine left='Potential Payout' right={payout} />
        {rewardText && <PromoBanner promoType='KambiReward' text={rewardText} />}
      </Flex>
    </>
  )
}

function getRewardText(type?: KambiRewardType) {
  switch (type) {
    case 'BEST_ODDS':
      return 'Best Odds'
    case 'FREE_BET':
      return 'Free Bet'
    case 'ODDS_BOOST':
      return 'Odds Boost'
    case 'PROFIT_BOOST':
      return 'Profit Boost'
    case 'SECOND_CHANCE':
      return 'Second Chance'

    default:
      return null
  }
}
